/**
 * Gamepad input (Phase 1). Polls the Web Gamepad API on the main thread and
 * publishes a Switch-style button bitmask + analog axes into a shared buffer
 * the CPU worker reads. The HLE `hid` service will consume this once the
 * interpreter executes guest code (Phase 2+); for now it validates the path.
 */

/** Layout of the shared input buffer, as Int32 words. */
export const INPUT_WORD_BUTTONS = 0;
export const INPUT_WORD_LX = 1;
export const INPUT_WORD_LY = 2;
export const INPUT_WORD_RX = 3;
export const INPUT_WORD_RY = 4;
export const INPUT_WORD_CONNECTED = 5;
export const INPUT_WORDS = 8;
export const INPUT_BYTES = INPUT_WORDS * 4;

/** Switch HidNpadButton bits (subset). */
export const NPAD_A = 1 << 0;
export const NPAD_B = 1 << 1;
export const NPAD_X = 1 << 2;
export const NPAD_Y = 1 << 3;
export const NPAD_STICK_L = 1 << 4;
export const NPAD_STICK_R = 1 << 5;
export const NPAD_L = 1 << 6;
export const NPAD_R = 1 << 7;
export const NPAD_ZL = 1 << 8;
export const NPAD_ZR = 1 << 9;
export const NPAD_PLUS = 1 << 10;
export const NPAD_MINUS = 1 << 11;
export const NPAD_LEFT = 1 << 12;
export const NPAD_UP = 1 << 13;
export const NPAD_RIGHT = 1 << 14;
export const NPAD_DOWN = 1 << 15;

/* Standard-mapping Gamepad button index -> Switch bit. Indices follow the
 * W3C "standard" gamepad; face buttons map by physical position (the bottom
 * face button is Switch B, the right is A, etc.). */
const BUTTON_MAP: ReadonlyArray<number> = [
  NPAD_B, NPAD_A, NPAD_Y, NPAD_X, /* 0..3 face */
  NPAD_L, NPAD_R, NPAD_ZL, NPAD_ZR, /* 4..7 shoulders/triggers */
  NPAD_MINUS, NPAD_PLUS, NPAD_STICK_L, NPAD_STICK_R, /* 8..11 */
  NPAD_UP, NPAD_DOWN, NPAD_LEFT, NPAD_RIGHT, /* 12..15 dpad */
];

/** A minimal structural view of a gamepad source (the Web Gamepad API or a
 *  mock). Returns the live pad snapshots, with disconnected slots as null. */
export type GamepadSource = () => (Gamepad | null)[];

/** Clamp a raw [-1,1] axis into the Switch's signed 16-bit stick range. The
 *  hardware reports sticks as int16; clamp guards against drivers that briefly
 *  report magnitudes slightly past 1.0. */
function packAxis(v: number): number {
  const n = Math.round(v * 32767);
  return n < -32768 ? -32768 : n > 32767 ? 32767 : n;
}

/**
 * Pure marshalling step: read pad #0 from `source`, fold its standard-mapping
 * buttons into the Switch HidNpadButton bitfield, pack sticks into int16, and
 * publish the snapshot into `view` (the shared input buffer) via Atomics. The
 * connected count goes to INPUT_WORD_CONNECTED. Returns the buttons bitfield
 * so callers/tests can observe the marshalling without re-reading the buffer.
 *
 * Extracted from the rAF loop so the device-independent packing is headlessly
 * testable against a mocked source (no DOM, no requestAnimationFrame).
 */
export function pollGamepadInto(view: Int32Array, source: GamepadSource): number {
  const raw = source();
  const pads = raw.filter((g): g is Gamepad => g !== null);
  const pad = pads[0];
  let buttons = 0;
  let lx = 0, ly = 0, rx = 0, ry = 0;

  if (pad) {
    for (let i = 0; i < pad.buttons.length && i < BUTTON_MAP.length; i++) {
      if (pad.buttons[i].pressed) buttons |= BUTTON_MAP[i];
    }
    const axis = (n: number): number =>
      pad.axes.length > n ? packAxis(pad.axes[n]) : 0;
    lx = axis(0); ly = axis(1); rx = axis(2); ry = axis(3);
  }

  Atomics.store(view, INPUT_WORD_BUTTONS, buttons);
  Atomics.store(view, INPUT_WORD_LX, lx);
  Atomics.store(view, INPUT_WORD_LY, ly);
  Atomics.store(view, INPUT_WORD_RX, rx);
  Atomics.store(view, INPUT_WORD_RY, ry);
  Atomics.store(view, INPUT_WORD_CONNECTED, pads.length);

  return buttons;
}

/** The browser's Gamepad source. Empty when the API is absent (e.g. SSR). */
const navigatorGamepads: GamepadSource = () => {
  const nav = navigator as Navigator & { getGamepads?: () => (Gamepad | null)[] };
  if (typeof nav.getGamepads !== "function") return [];
  return nav.getGamepads();
};

/**
 * Begin polling; returns a stop function. An optional `afterPoll` hook runs each
 * frame INSIDE the same rAF, immediately after the gamepad snapshot is written —
 * this is where a remote-controller receiver folds its input INTO the buffer
 * (OR-merging buttons, see remote/inject.ts), so the merge order is deterministic
 * rather than racing a second rAF loop. Default: no hook (pure gamepad).
 */
export function startGamepadPolling(
  input: SharedArrayBuffer,
  afterPoll?: (view: Int32Array) => void,
): () => void {
  const view = new Int32Array(input);
  let raf = 0;

  const poll = (): void => {
    pollGamepadInto(view, navigatorGamepads);
    if (afterPoll) afterPoll(view);
    raf = requestAnimationFrame(poll);
  };

  poll();
  return () => cancelAnimationFrame(raf);
}
